import React, { useEffect, useMemo, useState } from "react";
import {
  fetchRepLeaderboard, fetchRepDetail, registerRep, markRepPaid,
} from "../lib/api";

/**
 * AdminRepLeaderboard — real-time scoreboard for field-sales reps.
 *
 * Top section: global pace toward 1,000 paid in 60 days (daily target = 17).
 * Middle: a table of every rep sorted by paid signups today.
 * Tap a rep → expanded drawer with their full contractor list + per-payout
 *   buttons + bulk "Pay All Owed" action.
 *
 * The dashboard auto-refreshes every 30s so it works as a "Slack alarm" on
 * Lanre's desk during a Ferguson-supply-house morning.
 */
const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const GREEN = "#1A7A4A";
const RED = "#A32D2D";
const GREY = "#5a5d66";
const BG = "#F8F9FA";

const cents = (n) => `$${((n || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const formatTs = (t) => {
  if (!t) return "—";
  try {
    return new Date(t).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return t; }
};

const AdminRepLeaderboard = () => {
  const [board, setBoard] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openSlug, setOpenSlug] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showAddRep, setShowAddRep] = useState(false);
  const [newRep, setNewRep] = useState({ slug: "", display_name: "", commission_per_paid_cents: 5000 });
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = async () => {
    setErr(null);
    try {
      const data = await fetchRepLeaderboard();
      setBoard(data);
    } catch (e) {
      setErr("Couldn't load leaderboard. Check backend is reachable.");
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  const loadDetail = async (slug) => {
    setOpenSlug(slug);
    setDetail(null);
    setDetailLoading(true);
    try {
      const d = await fetchRepDetail(slug);
      setDetail(d);
    } catch (e) {
      setErr(`Couldn't load ${slug}.`);
    } finally {
      setDetailLoading(false);
    }
  };

  const onAddRep = async () => {
    if (!newRep.slug.trim() || !newRep.display_name.trim()) {
      alert("Slug and display name are required.");
      return;
    }
    setBusy(true);
    try {
      await registerRep({
        slug: newRep.slug.trim().toLowerCase(),
        display_name: newRep.display_name.trim(),
        commission_per_paid_cents: Number(newRep.commission_per_paid_cents) || 5000,
      });
      setShowAddRep(false);
      setNewRep({ slug: "", display_name: "", commission_per_paid_cents: 5000 });
      await load();
    } catch (e) {
      alert("Couldn't add rep. Try a different slug.");
    } finally {
      setBusy(false);
    }
  };

  const payAllForRep = async (slug) => {
    if (!window.confirm(`Mark ALL owed commissions for ${slug} as paid?`)) return;
    setBusy(true);
    try {
      const r = await markRepPaid({ slug, method: "manual" });
      await load();
      if (openSlug === slug) await loadDetail(slug);
      alert(`Paid ${r.paid_count} commissions (${cents(r.paid_amount_cents)}).`);
    } finally {
      setBusy(false);
    }
  };

  const payOne = async (contractorId) => {
    setBusy(true);
    try {
      await markRepPaid({ contractor_id: contractorId, method: "manual" });
      await load();
      if (openSlug) await loadDetail(openSlug);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard?.writeText(url);
    alert(`Copied:\n${url}`);
  };

  const exportCsv = () => {
    if (!board?.reps?.length) return;
    const header = "slug,display_name,paid_today,paid_week,paid_month,paid_total,mrr_dollars,commission_owed_dollars,commission_paid_dollars,last_signup\n";
    const rows = board.reps.map((r) => [
      r.slug, `"${(r.display_name || "").replace(/"/g, '""')}"`,
      r.paid_signups.today, r.paid_signups.week, r.paid_signups.month, r.paid_signups.total,
      (r.mrr_contributed_cents / 100).toFixed(0),
      (r.commission_owed_cents / 100).toFixed(0),
      (r.commission_paid_cents / 100).toFixed(0),
      r.last_signup_at || "",
    ].join(","));
    const csv = header + rows.join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `nearnow-rep-leaderboard-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const g = board?.global;
  const progressWidth = useMemo(
    () => Math.min(100, g?.daily_progress_pct || 0),
    [g?.daily_progress_pct]
  );

  return (
    <div data-testid="admin-rep-leaderboard-page" style={{ minHeight: "100vh", background: BG, padding: "32px 24px 80px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <p style={{ color: GOLD, letterSpacing: 4, fontSize: 13, fontFamily: "Bebas Neue, sans-serif", margin: 0 }}>
              ADMIN · LANRE COMMAND CENTER
            </p>
            <h1 className="font-display" style={{ fontSize: 56, color: NAVY, letterSpacing: 1, margin: "6px 0 0" }}>
              REP LEADERBOARD
            </h1>
            <p style={{ color: GREY, fontSize: 16, marginTop: 2 }}>
              Daily numbers + commissions owed across every field-sales rep. Updates every 30s.
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <label style={{ fontSize: 13, color: GREY, display: "flex", gap: 6, alignItems: "center" }}>
              <input
                data-testid="auto-refresh-toggle"
                type="checkbox" checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh
            </label>
            <button
              data-testid="refresh-btn"
              onClick={load} className="btn-outline-navy tap"
              style={{ minHeight: 44 }}
            >
              ↻ REFRESH
            </button>
            <button
              data-testid="export-csv-btn"
              onClick={exportCsv} className="btn-outline-navy tap"
              style={{ minHeight: 44 }}
            >
              ⬇ CSV
            </button>
            <button
              data-testid="add-rep-btn"
              onClick={() => setShowAddRep(!showAddRep)} className="btn-gold tap"
              style={{ minHeight: 44 }}
            >
              + ADD REP
            </button>
          </div>
        </div>

        {err && (
          <div data-testid="leaderboard-error" style={{
            marginTop: 16, padding: 14, borderRadius: 8,
            background: "#fde7e7", color: RED, fontSize: 15,
          }}>
            {err}
          </div>
        )}

        {/* Add rep form */}
        {showAddRep && (
          <div data-testid="add-rep-form" style={{
            marginTop: 18, background: "#fff", padding: 22, borderRadius: 12,
            boxShadow: "0 12px 30px rgba(10,22,40,0.08)", border: `2px solid ${GOLD}`,
          }}>
            <h3 style={{ margin: 0, fontSize: 18, color: NAVY }}>Add a new rep</h3>
            <p style={{ fontSize: 13, color: GREY, marginTop: 4 }}>
              The slug is what goes in the URL. Lowercase, hyphens. Example: <code>thomas</code> or <code>mike-r</code>.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 12, marginTop: 12, alignItems: "end" }}>
              <div>
                <label style={lbl}>URL Slug</label>
                <input
                  data-testid="new-rep-slug"
                  style={inp} placeholder="thomas"
                  value={newRep.slug}
                  onChange={(e) => setNewRep({ ...newRep, slug: e.target.value })}
                />
              </div>
              <div>
                <label style={lbl}>Display Name</label>
                <input
                  data-testid="new-rep-name"
                  style={inp} placeholder="Thomas Thomaidis"
                  value={newRep.display_name}
                  onChange={(e) => setNewRep({ ...newRep, display_name: e.target.value })}
                />
              </div>
              <div>
                <label style={lbl}>Commission per paid (cents)</label>
                <input
                  data-testid="new-rep-commission"
                  type="number" min={0} step={100} style={inp}
                  value={newRep.commission_per_paid_cents}
                  onChange={(e) => setNewRep({ ...newRep, commission_per_paid_cents: e.target.value })}
                />
              </div>
              <button
                data-testid="save-rep-btn"
                onClick={onAddRep}
                disabled={busy} className="btn-gold tap"
                style={{ minHeight: 48, paddingLeft: 22, paddingRight: 22 }}
              >
                SAVE
              </button>
            </div>
          </div>
        )}

        {/* Global pace */}
        {g && (
          <div data-testid="global-pace-strip" style={{
            marginTop: 22, background: NAVY, color: "#fff", borderRadius: 14, padding: 22,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 14 }}>
              <div>
                <div style={{ color: GOLD, letterSpacing: 3, fontSize: 13, fontFamily: "Bebas Neue, sans-serif" }}>
                  TODAY&apos;S PACE
                </div>
                <div className="font-display" style={{ fontSize: 64, lineHeight: 1, marginTop: 4 }}>
                  {g.paid_signups_today}<span style={{ color: GREY, fontSize: 28 }}> / {g.daily_target}</span>
                </div>
                <div style={{ fontSize: 13, color: "#cbd0db", marginTop: 6 }}>
                  Paid signups today · target = {g.daily_target}/day to hit 1,000 in 60 days
                </div>
                <div style={{ marginTop: 12, background: "rgba(255,255,255,0.08)", height: 10, borderRadius: 999, overflow: "hidden" }}>
                  <div
                    data-testid="daily-progress-bar"
                    style={{
                      width: `${progressWidth}%`, height: "100%",
                      background: progressWidth >= 100 ? GREEN : GOLD,
                      transition: "width 0.4s ease",
                    }}
                  />
                </div>
              </div>
              <Stat label="ALL-TIME PAID" value={g.paid_signups_total} testid="stat-all-time-paid" />
              <Stat label="MRR FROM REPS" value={cents(g.mrr_cents)} testid="stat-rep-mrr" />
              <Stat label="OWED · PAYABLE" value={cents(g.commission_owed_cents)} color={RED} testid="stat-owed" />
              <Stat label="PAID OUT" value={cents(g.commission_paid_cents)} color={GREEN} testid="stat-paid-out" />
              <Stat label="ACTIVE REPS" value={g.rep_count} testid="stat-rep-count" />
            </div>
          </div>
        )}

        {/* Leaderboard table */}
        <div style={{ marginTop: 26, background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 16px 40px rgba(10,22,40,0.08)" }}>
          <div style={{ padding: "16px 22px", borderBottom: `1px solid ${BG}`, display: "flex", justifyContent: "space-between" }}>
            <div className="font-display" style={{ fontSize: 22, color: NAVY, letterSpacing: 1 }}>
              ALL REPS
            </div>
            <div style={{ fontSize: 13, color: GREY }}>
              {board ? `${board.reps.length} reps · as of ${formatTs(board.as_of)}` : "Loading…"}
            </div>
          </div>

          {!board && !err && (
            <div data-testid="leaderboard-loading" style={{ padding: 40, textAlign: "center", color: GREY }}>
              Loading leaderboard…
            </div>
          )}

          {board && board.reps.length === 0 && (
            <div data-testid="leaderboard-empty" style={{ padding: 40, textAlign: "center" }}>
              <div style={{ fontSize: 18, color: NAVY, fontWeight: 600 }}>No reps yet.</div>
              <p style={{ color: GREY, marginTop: 4 }}>
                Tap <strong>+ ADD REP</strong> to register your first field-sales rep.
              </p>
            </div>
          )}

          {board && board.reps.length > 0 && (
            <table data-testid="leaderboard-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: BG }}>
                  <Th>REP</Th>
                  <Th align="right">TODAY</Th>
                  <Th align="right">WEEK</Th>
                  <Th align="right">MONTH</Th>
                  <Th align="right">TOTAL PAID</Th>
                  <Th align="right">MRR</Th>
                  <Th align="right">OWED</Th>
                  <Th align="right">PAID</Th>
                  <Th align="right">LAST</Th>
                  <Th align="right">ACTIONS</Th>
                </tr>
              </thead>
              <tbody>
                {board.reps.map((r) => {
                  const isOpen = openSlug === r.slug;
                  return (
                    <React.Fragment key={r.slug}>
                      <tr
                        data-testid={`rep-row-${r.slug}`}
                        style={{ borderTop: `1px solid ${BG}`, cursor: "pointer",
                                 background: isOpen ? "#fffaeb" : "transparent" }}
                        onClick={() => (isOpen ? setOpenSlug(null) : loadDetail(r.slug))}
                      >
                        <td style={cell}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{
                              width: 10, height: 10, borderRadius: 999,
                              background: r.active ? GREEN : GREY,
                            }} />
                            <div>
                              <div style={{ fontWeight: 700, color: NAVY, fontSize: 16 }}>
                                {r.display_name || r.slug}
                              </div>
                              <div style={{ fontSize: 12, color: GREY }}>
                                ?rep={r.slug} · {cents(r.commission_per_paid_cents)}/paid
                                {r.auto_discovered ? " · auto-discovered" : ""}
                              </div>
                            </div>
                          </div>
                        </td>
                        <Cell align="right" big>{r.paid_signups.today}</Cell>
                        <Cell align="right">{r.paid_signups.week}</Cell>
                        <Cell align="right">{r.paid_signups.month}</Cell>
                        <Cell align="right" big>{r.paid_signups.total}</Cell>
                        <Cell align="right">{cents(r.mrr_contributed_cents)}</Cell>
                        <Cell align="right">
                          <span style={{ color: r.commission_owed_cents > 0 ? RED : GREY, fontWeight: 700 }}>
                            {cents(r.commission_owed_cents)}
                          </span>
                        </Cell>
                        <Cell align="right">
                          <span style={{ color: GREEN }}>{cents(r.commission_paid_cents)}</span>
                        </Cell>
                        <Cell align="right" small>{formatTs(r.last_signup_at)}</Cell>
                        <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
                          {r.commission_owed_cents > 0 && (
                            <button
                              data-testid={`pay-all-${r.slug}`}
                              onClick={(e) => { e.stopPropagation(); payAllForRep(r.slug); }}
                              disabled={busy}
                              className="btn-gold tap"
                              style={{ minHeight: 36, fontSize: 13, padding: "0 12px" }}
                            >
                              PAY ALL
                            </button>
                          )}
                          <button
                            data-testid={`copy-link-${r.slug}`}
                            onClick={(e) => { e.stopPropagation(); copyLink(r.share_links.sales_kit); }}
                            className="btn-outline-navy tap"
                            style={{ minHeight: 36, fontSize: 13, padding: "0 12px", marginLeft: 6 }}
                          >
                            COPY LINK
                          </button>
                        </td>
                      </tr>

                      {isOpen && (
                        <tr data-testid={`rep-detail-${r.slug}`}>
                          <td colSpan={10} style={{ background: "#fffaeb", padding: 22 }}>
                            <RepDetail
                              detail={detail} loading={detailLoading}
                              onPayOne={payOne}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Helpers ----
const Stat = ({ label, value, color, testid }) => (
  <div data-testid={testid} style={{ minWidth: 120 }}>
    <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontFamily: "Bebas Neue, sans-serif" }}>
      {label}
    </div>
    <div className="font-display" style={{ fontSize: 36, lineHeight: 1.1, marginTop: 6, color: color || "#fff" }}>
      {value}
    </div>
  </div>
);

const RepDetail = ({ detail, loading, onPayOne }) => {
  if (loading || !detail) {
    return <div style={{ color: GREY }}>Loading detail…</div>;
  }
  const items = detail.contractors || [];
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <div className="font-display" style={{ fontSize: 22, color: NAVY, letterSpacing: 1 }}>
            {detail.display_name} · CONTRACTORS
          </div>
          <div style={{ fontSize: 13, color: GREY }}>
            {detail.signups.total} signups · {detail.paid_signups.total} paid · {cents(detail.commission_owed_cents)} owed
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <ShareLink label="SALES KIT" href={detail.share_links.sales_kit} />
          <ShareLink label="ONBOARD" href={detail.share_links.onboard_tablet} />
        </div>
      </div>

      {items.length === 0 ? (
        <p style={{ marginTop: 14, color: GREY }}>No contractors yet. Share the link above.</p>
      ) : (
        <table style={{ width: "100%", marginTop: 14, borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#fff" }}>
              <Th>NAME / BUSINESS</Th>
              <Th>TRADE · CITY</Th>
              <Th>PLAN</Th>
              <Th>PAID?</Th>
              <Th>COMMISSION</Th>
              <Th align="right">CREATED</Th>
              <Th align="right" />
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} data-testid={`contractor-row-${c.id}`} style={{ borderTop: "1px solid #f0f0f0" }}>
                <td style={cell}>
                  <div style={{ fontWeight: 600, color: NAVY }}>{c.owner_name || "—"}</div>
                  <div style={{ fontSize: 11, color: GREY }}>{c.business_name || c.email || c.phone}</div>
                </td>
                <td style={cell}>{(c.trade || "—")} · {c.city || "—"}</td>
                <td style={cell}>{(c.plan || "—").toUpperCase()}</td>
                <td style={cell}>
                  {c.stripe_cc_on_file
                    ? <span style={{ color: GREEN, fontWeight: 700 }}>✓ PAID</span>
                    : <span style={{ color: GREY }}>pipeline</span>}
                </td>
                <td style={cell}>
                  {c.commission_paid_at
                    ? <span style={{ color: GREEN }}>paid {formatTs(c.commission_paid_at)}</span>
                    : c.stripe_cc_on_file
                      ? <span style={{ color: RED, fontWeight: 700 }}>OWED</span>
                      : <span style={{ color: GREY }}>—</span>}
                </td>
                <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>{formatTs(c.created_at)}</td>
                <td style={{ ...cell, textAlign: "right" }}>
                  {c.stripe_cc_on_file && !c.commission_paid_at && (
                    <button
                      data-testid={`pay-one-${c.id}`}
                      onClick={() => onPayOne(c.id)}
                      className="btn-gold tap"
                      style={{ minHeight: 32, fontSize: 12, padding: "0 12px" }}
                    >
                      PAY {cents(detail.commission_per_paid_cents)}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const ShareLink = ({ label, href }) => (
  <a href={href} target="_blank" rel="noreferrer"
     style={{ fontSize: 13, color: NAVY, textDecoration: "underline",
              fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2 }}>
    {label} ↗
  </a>
);

const Th = ({ children, align }) => (
  <th style={{
    textAlign: align || "left", padding: "12px 14px",
    fontSize: 11, color: GREY, letterSpacing: 1.5, fontFamily: "Bebas Neue, sans-serif",
    borderBottom: `1px solid ${BG}`,
  }}>{children}</th>
);

const Cell = ({ children, align, big, small }) => (
  <td style={{
    ...cell, textAlign: align || "left",
    fontWeight: big ? 700 : 400,
    fontSize: big ? 18 : small ? 12 : 14,
    color: big ? NAVY : "#1A1A2E",
  }}>{children}</td>
);

const cell = { padding: "14px", verticalAlign: "middle" };
const lbl = { fontSize: 12, color: GREY, letterSpacing: 2, fontFamily: "Bebas Neue, sans-serif", display: "block", marginBottom: 4 };
const inp = {
  width: "100%", minHeight: 46, fontSize: 16, padding: "0 14px",
  border: `2px solid #cbd0db`, borderRadius: 8, background: "#fff",
  color: NAVY, outline: "none", boxSizing: "border-box",
};

export default AdminRepLeaderboard;
